import React, { useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaStore, FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchBranchDetails, clearError, clearBranchDetails } from '../../../redux/Slices/AdminStoresSlice';

const AdminStoreBranchDetails = () => {
    const { setTitle } = useTitle();
    const { id: storeId, branchId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { branchDetails: branch, branchDetailsLoading: loading, error } = useSelector((state) => state.adminStores);

    useEffect(() => {
        setTitle('تفاصيل الفرع');
        dispatch(fetchBranchDetails({ storeId, branchId }));
        
        return () => {
            dispatch(clearBranchDetails());
        };
    }, [setTitle, storeId, branchId, dispatch]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
            if (!branch) {
                navigate(`/admin-stores/${storeId}/branches`);
            }
        }
    }, [error, dispatch, branch, navigate, storeId]);

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success fs-6 text-white">نشط</span>;
        }
        return <span className="badge bg-danger fs-6 text-white">غير نشط</span>;
    };

    const getBanBadge = (ban) => {
        if (ban === "1" || ban === 1) {
            return <span className="badge bg-danger fs-6 text-white">محظور</span>;
        }
        return <span className="badge bg-success fs-6 text-white">غير محظور</span>;
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل الفرع...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!branch) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <FaStore className="text-muted" style={{ fontSize: '3rem' }} />
                        <p className="mt-3 text-muted">الفرع غير موجود</p>
                        <button className="btn btn-primary" onClick={() => navigate(`/admin-stores/${storeId}/branches`)}>
                            العودة للفروع
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate(`/admin-stores/${storeId}/branches`)}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة للفروع
                    </motion.button>
                </div>

                {/* Branch Details */}
                <div className="row">
                    {/* Basic Info */}
                    <div className="col-md-8">
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0">المعلومات الأساسية</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    {/* عرض Logo و Cover */}
                                    {(branch.logo || branch.cover) && (
                                        <div className="col-12 mb-4">
                                            <div className="row">
                                                {/* Logo */}
                                                {branch.logo && branch.logo !== 'logo' && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FaStore className="mx-2" />
                                                            شعار الفرع (Logo)
                                                        </label>
                                                        <div style={{
                                                            width: '200px',
                                                            height: '200px',
                                                            border: '2px solid #e9ecef',
                                                            borderRadius: '12px',
                                                            overflow: 'hidden',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                        }}>
                                                            <img
                                                                src={branch.logo}
                                                                alt="Branch Logo"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Cover */}
                                                {branch.cover && branch.cover !== 'cover' && (
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label fw-semibold">
                                                            🖼️ صورة الغلاف (Cover)
                                                        </label>
                                                        <div style={{
                                                            width: '100%',
                                                            maxWidth: '800px',
                                                            height: '300px',
                                                            border: '2px solid #e9ecef',
                                                            borderRadius: '12px',
                                                            overflow: 'hidden',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                        }}>
                                                            <img
                                                                src={branch.cover}
                                                                alt="Branch Cover"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <hr className="my-4" />
                                        </div>
                                    )}

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">اسم الفرع</label>
                                        <div className="d-flex align-items-center">
                                            <FaStore className="text-muted mx-2" />
                                            <span>{branch.market_name || branch.name || branch.branch_name || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">المتجر الرئيسي</label>
                                        <div className="d-flex align-items-center">
                                            <FaStore className="text-muted mx-2" />
                                            <span>{branch.parent_name || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">رقم الهاتف</label>
                                        <div className="d-flex align-items-center">
                                            <FaPhone className="text-muted mx-2" />
                                            <span>{branch.phone || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">رقم التواصل</label>
                                        <div className="d-flex align-items-center">
                                            <FaPhone className="text-muted mx-2" />
                                            <span>{branch.contact_number || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">البريد الإلكتروني</label>
                                        <div className="d-flex align-items-center">
                                            <FaEnvelope className="text-muted mx-2" />
                                            <span>{branch.email || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">التصنيف</label>
                                        <div className="d-flex align-items-center">
                                            <span>{branch.category?.name || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">التقييم</label>
                                        <div className="d-flex align-items-center">
                                            <span>{branch.rate_avg || 'لا يوجد تقييم'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">حالة النشاط</label>
                                        <div className="d-flex align-items-center">
                                            {getStatusBadge(branch.status)}
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">حالة الحظر</label>
                                        <div className="d-flex align-items-center">
                                            {getBanBadge(branch.ban)}
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <label className="form-label fw-semibold">العنوان</label>
                                        <div className="d-flex align-items-center">
                                            <FaMapMarkerAlt className="text-muted mx-2" />
                                            <span>{branch.address || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    {branch.lat && branch.lng && (
                                        <div className="col-md-12 mb-3">
                                            <label className="form-label fw-semibold">الموقع على الخريطة</label>
                                            <div style={{ height: '350px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e9ecef', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                                <iframe
                                                    title="موقع الفرع"
                                                    width="100%"
                                                    height="100%"
                                                    frameBorder="0"
                                                    style={{ border: 0 }}
                                                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAHQSuklfkVa-mATYvbA7odk3_LpnKG-OA&q=${parseFloat(branch.lat).toFixed(7)},${parseFloat(branch.lng).toFixed(7)}&zoom=18&language=ar`}
                                                    allowFullScreen>
                                                </iframe>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mt-2">
                                                <small className="text-muted">
                                                    الإحداثيات: {parseFloat(branch.lat).toFixed(7)}, {parseFloat(branch.lng).toFixed(7)}
                                                </small>
                                                <a 
                                                    href={`https://www.google.com/maps?q=${parseFloat(branch.lat).toFixed(7)},${parseFloat(branch.lng).toFixed(7)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm text-white"
                                                    style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                                                    فتح في Google Maps
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">وقت البدء</label>
                                        <div className="d-flex align-items-center">
                                            <FaClock className="text-muted mx-2" />
                                            <span>{branch.start_time || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">وقت النهاية</label>
                                        <div className="d-flex align-items-center">
                                            <FaClock className="text-muted mx-2" />
                                            <span>{branch.end_time || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    {branch.description && (
                                        <div className="col-md-12 mb-3">
                                            <label className="form-label fw-semibold">الوصف</label>
                                            <div className="p-3 bg-light rounded">
                                                <p className="mb-0">{branch.description}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Branch Card */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">معلومات الفرع</h5>
                            </div>
                            <div className="card-body text-center">
                                <div className="mb-3">
                                    <div className="mx-auto mb-3" style={{ width: '120px', height: '120px' }}>
                                        {branch.logo && branch.logo !== 'logo' ? (
                                            <img
                                                src={branch.logo}
                                                alt={branch.market_name || branch.name || 'فرع'}
                                                className="w-100 h-100 rounded"
                                                style={{ objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div className="bg-primary text-white d-flex align-items-center justify-content-center w-100 h-100 rounded">
                                                <FaStore style={{ fontSize: '3rem' }} />
                                            </div>
                                        )}
                                    </div>
                                    <h6>{branch.market_name || branch.name || branch.branch_name || 'غير محدد'}</h6>
                                    <p className="text-muted">{branch.email || 'غير محدد'}</p>
                                </div>
                                
                                <div className="mb-3">
                                    <div className="d-flex justify-content-around">
                                        <div>
                                            <small className="text-muted">حالة النشاط</small>
                                            <div className="mt-1">{getStatusBadge(branch.status)}</div>
                                        </div>
                                        <div>
                                            <small className="text-muted">حالة الحظر</small>
                                            <div className="mt-1">{getBanBadge(branch.ban)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminStoreBranchDetails;

